import * as Sentry from "@sentry/react";
import axios from "axios";

// Read directly: src/config pulls in i18n, which must not load before Sentry.
const TTP_API_URL = process.env.REACT_APP_TTP_API_URL;

const FILTERED = "[Filtered]";
// Query params holding credentials (access_token, token, params = encrypted auth payload, ...).
const SENSITIVE_PARAM = /token|auth|password|secret|key|^params$/i;
const SENSITIVE_HEADER = /^(authorization|cookie|set-cookie|x-.*token.*)$/i;

const scrubUrl = (url) => {
  if (typeof url !== "string" || !url.includes("?")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.forEach((_, name) => {
      if (SENSITIVE_PARAM.test(name)) parsed.searchParams.set(name, FILTERED);
    });
    return url.startsWith("http") ? parsed.href : parsed.pathname + parsed.search;
  } catch (e) {
    return url.split("?")[0];
  }
};

const scrubHeaders = (headers) => {
  if (!headers) return headers;
  Object.keys(headers).forEach((name) => {
    if (SENSITIVE_HEADER.test(name)) headers[name] = FILTERED;
    else if (/bearer\s/i.test(headers[name])) headers[name] = FILTERED;
    else if (/^referer$/i.test(name)) headers[name] = scrubUrl(headers[name]);
  });
  return headers;
};

const scrubRequest = (request) => {
  if (!request) return;
  request.url = scrubUrl(request.url);
  delete request.query_string;
  delete request.cookies;
  scrubHeaders(request.headers);
};

const isHttpClientEvent = (event) =>
  event.exception?.values?.[0]?.mechanism?.type?.startsWith("auto.http.client");

Sentry.init({
  dsn: "https://2ceeb0fd63796991041f79a2fdbe62bb@o4506798074232832.ingest.us.sentry.io/4512215574773760",
  environment: process.env.REACT_APP_APP_ENV,

  integrations: [
    // Reports failed fetch and XHR (axios) responses. Other HTTP errors
    // (401, 404, ...) are expected and stay out to keep event volume low.
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [403, [500, 599]],
    }),
  ],

  beforeSend(event) {
    scrubRequest(event.request);
    if (event.contexts?.response) {
      scrubHeaders(event.contexts.response.headers);
      delete event.contexts.response.cookies;
    }

    // Default title is "HTTP Client Error with status code: 500" for every
    // endpoint: name and group each issue by method + path + status instead.
    if (isHttpClientEvent(event)) {
      const status = event.contexts?.response?.status_code;
      const method = (event.request?.method || "GET").toUpperCase();
      const path = (event.request?.url || "").split("?")[0];
      const title = `HTTP ${status} on ${method} ${path}`;
      event.message = title;
      event.exception.values[0].value = title;
      event.fingerprint = ["http-client-error", method, path, String(status)];
    }
    return event;
  },

  beforeBreadcrumb(breadcrumb) {
    const data = breadcrumb.data;
    if (data) {
      ["url", "from", "to"].forEach((key) => {
        if (data[key]) data[key] = scrubUrl(data[key]);
      });
    }
    return breadcrumb;
  },
});

// httpClientIntegration only sees responses the browser hands to JS. A response
// blocked by CORS (e.g. a 500 without Access-Control-Allow-Origin) reaches axios
// as a bare "Network Error" with no status: report those per endpoint, for the
// TamTam API only (other hosts would mostly report users' flaky connections).
export const captureNetworkErrors = (instance) => {
  instance.interceptors.response.use(undefined, (error) => {
    const isNetworkError =
      !error?.response &&
      error?.request &&
      error.message === "Network Error" &&
      !axios.isCancel(error) &&
      navigator.onLine !== false;

    const { method = "get", baseURL = "", url = "" } = error?.config || {};
    const fullUrl = /^https?:\/\//.test(url) ? url : baseURL + url;
    const isTamTamApi = !!TTP_API_URL && fullUrl.startsWith(TTP_API_URL);

    if (isNetworkError && isTamTamApi) {
      const path = fullUrl.split("?")[0];
      const endpoint = `${method.toUpperCase()} ${path}`;

      Sentry.captureException(new Error(`Network error on ${endpoint}`), {
        fingerprint: ["http-network-error", endpoint],
        tags: { "http.network_error": true },
      });
    }
    return Promise.reject(error);
  });
};

captureNetworkErrors(axios);
